import React, { useState, cloneElement, ReactElement } from 'react'
import { usePlanPermissions } from '../../../hooks/usePlanPermissions'
import { USE_NEW_PLAN_LOGIC } from '../../utils/constant'
import ChangePlanModal from './ChangePlanModal'
import ContractAddonsModal from './ContractAddonsModal'
import { Dialog, DialogContent } from '@mui/material'
import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import CustomButton from './CustomButton'
import CloseIcon from '@mui/icons-material/CloseOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'

const CustomTooltip = styled(({ className, ...props }: TooltipProps & { className?: string }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#000033',
    color: '#fff',
    fontSize: '0.75rem',
    padding: '8px 12px',
    borderRadius: '6px',
    maxWidth: '300px',
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: '#000033'
  }
}))

interface PlanRestrictedButtonProps {
  children: ReactElement
  requiredPermission?: string
  requiredSupport?: string
  requiredReport?: string
  requiredDashboard?: string
  checkBusinessLimit?: boolean
  checkBankLimit?: boolean
  reportId?: string
  dashboardType?: 'basic' | 'full'
  message?: string
  className?: string
  disabled?: boolean
  onClick?: (e: React.MouseEvent) => void
  keepEnabled?: boolean
  canAddAddon?: boolean
}

const PlanRestrictedButton = ({
  children,
  requiredPermission,
  requiredSupport,
  requiredReport,
  requiredDashboard,
  checkBusinessLimit = false,
  checkBankLimit = false,
  reportId,
  dashboardType,
  message = '',
  className = '',
  disabled: externalDisabled = false,
  onClick,
  keepEnabled = false,
  canAddAddon = false,
  ...buttonProps
}: PlanRestrictedButtonProps) => {
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [showChangePlanModal, setShowChangePlanModal] = useState(false)
  const [showAddonsModal, setShowAddonsModal] = useState(false)
  const { hasPermission, hasSupportAccess, hasReportAccess, hasDashboardAccess, hasPlan, subscriptionExpired, canAddBusiness, canAddBank, canAccessReport, canAccessDashboard, canContractAddons } = usePlanPermissions()

  const childOnClick = React.isValidElement(children)
    ? (children.props as { onClick?: (e: React.MouseEvent) => void })?.onClick
    : undefined
  const childDisabled = React.isValidElement(children)
    ? Boolean((children.props as { disabled?: boolean })?.disabled)
    : false
  const effectiveOnClick = onClick ?? childOnClick

  if (!USE_NEW_PLAN_LOGIC) {
    return React.isValidElement(children)
      ? cloneElement(children, { disabled: externalDisabled || childDisabled, onClick: effectiveOnClick, ...buttonProps } as React.HTMLAttributes<HTMLElement>)
      : <div className={className}>{children}</div>
  }

  const getFullMessage = () => {
    const standardSuffix = 'no están disponibles en tu plan actual. Actualiza tu plan para acceder a esta funcionalidad.'

    if (!message) {
      return `Esta funcionalidad ${standardSuffix}`
    }

    if (message.includes(standardSuffix) || message.includes('no está disponible')) {
      return message
    }

    return `${message} ${standardSuffix}`
  }

  const fullMessage = getFullMessage()

  const hasAccess = () => {
    if (!hasPlan) return false
    if (subscriptionExpired) return false

    if (dashboardType) {
      return canAccessDashboard(dashboardType)
    }

    if (reportId) {
      return canAccessReport(reportId)
    }

    if (checkBusinessLimit) {
      return canAddBusiness()
    }

    if (checkBankLimit) {
      return canAddBank()
    }

    if (requiredPermission) {
      return hasPermission(requiredPermission as any)
    }

    if (requiredSupport) {
      return hasSupportAccess(requiredSupport)
    }

    if (requiredReport) {
      return hasReportAccess(requiredReport)
    }

    if (requiredDashboard) {
      return hasDashboardAccess(requiredDashboard)
    }

    return true
  }

  const access = hasAccess()
  const isDisabled = (keepEnabled ? externalDisabled : (externalDisabled || !access)) || childDisabled

  const handleClick = (e: React.MouseEvent) => {
    if (!access) {
      e.preventDefault()
      e.stopPropagation()
      setShowInfoModal(true)
      return
    }

    if (effectiveOnClick) {
      effectiveOnClick(e)
    }
  }

  const handlePlanUpdated = () => {
    setShowChangePlanModal(false)
    setShowInfoModal(false)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowInfoModal(true)
  }

  const handleOpenChangePlan = () => {
    setShowInfoModal(false)
    setShowChangePlanModal(true)
  }

  const handleOpenAddons = () => {
    setShowInfoModal(false)
    setShowAddonsModal(true)
  }

  const getPreferredAddonPermission = (): string | undefined => {
    if (checkBusinessLimit) return 'maxBusinesses'
    if (checkBankLimit) return 'maxBanks'
    return requiredPermission
  }

  return (
    <>
      <CustomTooltip
        title={!access ? fullMessage : ''}
        arrow
        placement="top"
        disableHoverListener={access}
      >
        <div className={`relative ${className}`}>
          {React.isValidElement(children)
            ? cloneElement(children, {
                disabled: isDisabled,
                onClick: handleClick,
                ...buttonProps
              } as React.HTMLAttributes<HTMLElement>)
            : <div className={className}>{children}</div>}
          {!keepEnabled && !access && (
            <>
              <div
                className="absolute inset-0 bg-white/62 backdrop-blur rounded-2xl cursor-pointer z-10"
                onClick={handleOverlayClick}
              />
              <div
                className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer z-20"
                onClick={handleOverlayClick}
              >
                <LockOutlinedIcon className="h-8 w-8 text-gray-500" />
              </div>
            </>
          )}
        </div>
      </CustomTooltip>

      <Dialog
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent className='bg-gray-50 p-6 rounded-lg'>
          <div className='flex justify-between items-center mb-4 border-b pb-2'>
            <h3 className='text-xl font-bold'>Funcionalidad no disponible</h3>
            <button
              className='text-gray-500 hover:text-gray-700'
              onClick={() => setShowInfoModal(false)}
            >
              <CloseIcon />
            </button>
          </div>

          <div className='py-4'>
            <div className='mb-4'>
              <div className='flex items-center justify-center mb-4'>
                <LockOutlinedIcon className="h-16 w-16 text-gray-400" />
              </div>
              <p className='text-sm text-gray-600 text-center mb-4'>
                {fullMessage}
              </p>
            </div>
          </div>

          <div className='flex justify-end mt-6 gap-3 flex-wrap'>
            <CustomButton
              title='Cancelar'
              color='primary'
              className='px-4 py-2 rounded-full'
              onClick={() => setShowInfoModal(false)}
              imageSrc={undefined}
              disabled={false}
              imgStyle={undefined}
            />
            {canAddAddon && canContractAddons && (
              <CustomButton
                title='Contratar Add-on'
                color='accent2'
                className='px-4 py-2 rounded-full'
                onClick={handleOpenAddons}
                imageSrc={undefined}
                disabled={false}
                imgStyle={undefined}
              />
            )}
            <CustomButton
              title='Cambiar Plan'
              color='accent2'
              className='px-4 py-2 rounded-full'
              onClick={handleOpenChangePlan}
              imageSrc={undefined}
              disabled={false}
              imgStyle={undefined}
            />
          </div>
        </DialogContent>
      </Dialog>

      <ChangePlanModal
        isOpen={showChangePlanModal}
        onClose={() => setShowChangePlanModal(false)}
        onPlanUpdated={handlePlanUpdated}
        filterPlanBy={{
          ...(requiredPermission && { permission: requiredPermission }),
          ...(requiredSupport && { support: requiredSupport }),
          ...(requiredReport && { report: requiredReport }),
          ...(requiredDashboard && { dashboard: requiredDashboard }),
        }}
      />
      <ContractAddonsModal
        isOpen={showAddonsModal}
        onClose={() => setShowAddonsModal(false)}
        onSuccess={handlePlanUpdated}
        preferredAddonPermission={getPreferredAddonPermission()}
      />
    </>
  )
}

export default PlanRestrictedButton
